import { useEffect, useState } from "react";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import Navbar from "../components/NavBar";
import { Collections, CollectionsClass } from "../utils/firestore/collections";
import { handleErrors } from "../utils/firestore/handleErrors";
import Article, { ArticleData } from "../models/articles/article.model";
import LoadingView from "../components/LoadingView";
import { useAppSelector } from "../store/store";
import { FaPlus, FaTrash } from "react-icons/fa";

const ARTICLES_DOC_ID = "articles";

const ArticlesPage = () => {
    const { user } = useAppSelector((s) => s.auth);
    const isAdmin = user?.role === "admin";

    const [isLoading, setIsLoading] = useState(true);
    const [articles, setArticles] = useState<Article[]>([]);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [saving, setSaving] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newArticle, setNewArticle] = useState({ title: "", description: "", url: "", image_url: "" });

    const docRef = doc(CollectionsClass.instance, Collections.ARTICLES, ARTICLES_DOC_ID);

    useEffect(() => {
        setError("");
        getDoc(docRef)
            .then((res) => {
                const data = res.data() as ArticleData | undefined;
                setArticles(data?.data ?? []);
            })
            .catch((e) => setError(handleErrors(e)))
            .finally(() => setIsLoading(false));
    }, []);

    const openArticle = (url: string) => {
        window.open(url, "_blank");
    };

    const handleAddArticle = (e: React.FormEvent) => {
        e.preventDefault();
        setActionError("");
        const { title, description, url, image_url } = newArticle;
        if (!title.trim() || !url.trim()) {
            setActionError("Titulli dhe linku janë të detyrueshëm.");
            return;
        }
        setSaving(true);
        const added: Article = {
            id: `article_${Date.now()}`,
            title: title.trim(),
            description: (description ?? "").trim(),
            url: url.trim(),
            image_url: (image_url ?? "").trim(),
        };
        const nextData = [...articles, added];
        updateDoc(docRef, { data: nextData })
            .then(() => {
                setArticles(nextData);
                setNewArticle({ title: "", description: "", url: "", image_url: "" });
                setShowAddForm(false);
            })
            .catch((e) => setActionError(handleErrors(e)))
            .finally(() => setSaving(false));
    };

    const handleRemoveArticle = (id: string) => {
        if (!window.confirm("A jeni të sigurt që dëshironi ta hiqni këtë artikull?")) return;
        setActionError("");
        setSaving(true);
        const nextData = articles.filter((a) => a.id !== id);
        updateDoc(docRef, { data: nextData })
            .then(() => setArticles(nextData))
            .catch((e) => setActionError(handleErrors(e)))
            .finally(() => setSaving(false));
    };

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-4xl mx-auto px-4 py-6">
                <h1 className="text-2xl font-semibold text-[#704e37] mb-6">Artikujt</h1>

                {isAdmin && (
                    <section className="mb-8 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-lg font-semibold text-gray-800">Menaxhimi i artikujve</h2>
                            {!showAddForm ? (
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(true)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#704e37] text-white font-medium text-sm hover:opacity-90"
                                >
                                    <FaPlus /> Shto artikull
                                </button>
                            ) : null}
                        </div>
                        {actionError && <p className="text-red-500 text-sm mb-3">{actionError}</p>}
                        {showAddForm && (
                            <form onSubmit={handleAddArticle} className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Titulli *</label>
                                    <input
                                        type="text"
                                        value={newArticle.title}
                                        onChange={(e) => setNewArticle((p) => ({ ...p, title: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                                        placeholder="Titulli i artikullit"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Përshkrimi</label>
                                    <textarea
                                        value={newArticle.description}
                                        onChange={(e) => setNewArticle((p) => ({ ...p, description: e.target.value }))}
                                        rows={2}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                                        placeholder="Përshkrim i shkurtër"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Linku *</label>
                                    <input
                                        type="url"
                                        value={newArticle.url}
                                        onChange={(e) => setNewArticle((p) => ({ ...p, url: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">URL e figurës</label>
                                    <input
                                        type="url"
                                        value={newArticle.image_url}
                                        onChange={(e) => setNewArticle((p) => ({ ...p, image_url: e.target.value }))}
                                        className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#704e37]/30 focus:border-[#704e37] outline-none"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-4 py-2 rounded-lg bg-[#704e37] text-white font-medium text-sm hover:opacity-90 disabled:opacity-50"
                                    >
                                        {saving ? "Duke ruajtur..." : "Ruaj"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setShowAddForm(false); setNewArticle({ title: "", description: "", url: "", image_url: "" }); setActionError(""); }}
                                        className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                                    >
                                        Anulo
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                )}

                {isLoading && <LoadingView size={50} />}
                {error && <p className="text-red-500">{error}</p>}
                {!isLoading && !error && (
                    <div className="space-y-4">
                        {articles.map((item) => (
                            <div
                                key={item.id}
                                className="w-full bg-white rounded-xl shadow overflow-hidden flex flex-col sm:flex-row group"
                            >
                                <button
                                    type="button"
                                    onClick={() => openArticle(item.url)}
                                    className="flex-1 flex text-left hover:shadow-md transition min-w-0"
                                >
                                    <div className="sm:w-48 h-32 sm:h-28 flex-shrink-0 bg-gray-100">
                                        {item.image_url ? (
                                            <img
                                                src={item.image_url}
                                                alt={item.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
                                        )}
                                    </div>
                                    <div className="p-4 flex-1">
                                        <h2 className="font-semibold text-gray-800">{item.title}</h2>
                                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                                    </div>
                                </button>
                                {isAdmin && (
                                    <div className="flex items-center p-2 sm:p-4 border-t sm:border-t-0 sm:border-l border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveArticle(item.id)}
                                            disabled={saving}
                                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                                            title="Hiq artikullin"
                                        >
                                            <FaTrash className="text-lg" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {!isLoading && !error && articles.length === 0 && (
                    <p className="text-gray-500">Nuk ka artikuj.</p>
                )}
            </div>
        </div>
    );
};

export default ArticlesPage;
